import { useCallback, useRef, useState } from "react";
import { Building2, TriangleAlert, Users } from "lucide-react";

import {
  type CarrierTeamMember,
  useCarrierMe,
  useCarrierTeam,
} from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CompanyProfileForm } from "@/features/company-profile-form";
import { hasHttpStatus } from "@/lib/api-error";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

type CarrierSummary = {
  carrierId: string;
  carrierName: string;
  carrierAdminCount: number;
  dispatcherCount: number;
};

export default function CompanySettingsScreen() {
  const [dirtyCarriers, setDirtyCarriers] = useState<ReadonlySet<string>>(
    new Set()
  );
  const onDirtyChange = useCallback((carrierId: string, dirty: boolean) => {
    setDirtyCarriers((current) => {
      if (current.has(carrierId) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(carrierId);
      else next.delete(carrierId);
      return next;
    });
  }, []);
  useUnsavedChangesGuard(
    dirtyCarriers.size > 0,
    "Leaving this page will discard your unsaved company-profile changes. Continue?"
  );
  const access = useCarrierMe({ refetchOnMount: "always" });
  const ownerCarrierIds = (access.data?.carriers ?? [])
    .filter((c) => c.roles.includes("CARRIER_OWNER"))
    .map((c) => c.id)
    .sort();
  const team = useCarrierTeam(undefined, {
    refetchOnMount: "always",
    ownerCarrierIds,
  });
  const teamRows: CarrierTeamMember[] = Array.isArray(team.data)
    ? (team.data as CarrierTeamMember[])
    : (team.data?.data ?? []);
  const carriers = access.data?.carriers ?? [];
  const summaries = buildSummaries(teamRows).filter((summary) =>
    carriers.some(
      (carrier) =>
        carrier.id === summary.carrierId &&
        carrier.roles.includes("CARRIER_OWNER")
    )
  );
  const accessPaused =
    !access.isFetchedAfterMount && access.fetchStatus === "paused";
  const checkingAccess =
    !accessPaused && (access.isLoading || !access.isFetchedAfterMount);
  const accessForbidden =
    hasHttpStatus(access.error, 401) || hasHttpStatus(access.error, 403);
  const teamForbidden = hasHttpStatus(team.error, 403);
  // Preserve mounted drafts only after this visit has verified their scope.
  const verifiedThisVisit = useRef(false);
  if (access.isFetchedAfterMount && access.data && !access.error) {
    verifiedThisVisit.current = true;
  }
  const retainProfiles =
    verifiedThisVisit.current && !!access.data && !accessForbidden;

  return (
    <div className="carrier-page">
      <div className="mx-auto max-w-4xl space-y-5">
        <PageHeader title="Company" />

        {checkingAccess && !access.error ? (
          <div role="status" aria-label="Checking carrier access">
            <Skeleton className="h-32" />
          </div>
        ) : access.error || !access.data || accessPaused ? (
          <div role="alert" className="space-y-3 text-sm text-destructive">
            <p>
              {accessForbidden
                ? "You do not have permission to view company settings."
                : "Carrier access could not be verified."}
            </p>
            {!accessForbidden && (
              <Button
                type="button"
                variant="outline"
                size="sm-touch"
                onClick={() => void access.refetch()}
              >
                Try again
              </Button>
            )}
          </div>
        ) : null}

        {retainProfiles &&
          carriers.map((carrier) => (
            <section
              key={carrier.id}
              hidden={!!access.error || accessPaused}
              aria-label={`Company profile for ${carrier.name ?? carrier.id}`}
            >
              <Card className="overflow-hidden">
                <CardHeader className="border-b p-4">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                      <Building2 className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {carriers.length === 1
                          ? "Company profile"
                          : (carrier.name ?? "Company profile")}
                      </CardTitle>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Used on labels, customer tracking, and EZX dispatch.
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <CompanyProfileForm
                    carrierId={carrier.id}
                    canEdit={
                      !access.error && carrier.roles.includes("CARRIER_OWNER")
                    }
                    onDirtyChange={onDirtyChange}
                  />
                </CardContent>
              </Card>
            </section>
          ))}

        <Card className="overflow-hidden">
          <CardHeader className="border-b p-4">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Users className="h-4 w-4" aria-hidden="true" />
              </div>
              <div>
                <CardTitle className="text-base">Team summary</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  People with access to your carriers.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {access.error || accessPaused ? null : teamForbidden ? (
              <TeamError
                forbidden
                onRetry={() => {
                  void access.refetch();
                  void team.refetch();
                }}
              />
            ) : checkingAccess || team.isFetching || team.isLoading ? (
              <TeamLoading />
            ) : team.error || team.fetchStatus === "paused" ? (
              <TeamError
                forbidden={false}
                onRetry={() => {
                  void access.refetch();
                  void team.refetch();
                }}
              />
            ) : summaries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No carrier memberships found for this account.
              </p>
            ) : (
              <div className="space-y-2">
                {summaries.map((summary) => (
                  <TeamSummary key={summary.carrierId} summary={summary} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TeamSummary({ summary }: { summary: CarrierSummary }) {
  return (
    <article className="flex min-w-0 items-start gap-3 rounded-md border bg-background p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
        {initials(summary.carrierName)}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="break-words text-sm font-semibold">
          {summary.carrierName}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {summary.carrierAdminCount} carrier admin
          {summary.carrierAdminCount === 1 ? "" : "s"} ·{" "}
          {summary.dispatcherCount} dispatcher
          {summary.dispatcherCount === 1 ? "" : "s"}
        </p>
        <p className="mt-1 break-all font-mono text-[10px] text-muted-foreground/70">
          Workspace {summary.carrierId}
        </p>
      </div>
    </article>
  );
}

function TeamLoading() {
  return (
    <div aria-label="Loading team summary" role="status" className="space-y-2">
      <Skeleton className="h-16" />
      <Skeleton className="h-16" />
    </div>
  );
}

function TeamError({
  forbidden,
  onRetry,
}: {
  forbidden: boolean;
  onRetry: () => void;
}) {
  return (
    <div role="alert" className="space-y-3">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {forbidden
            ? "You do not have permission to view the team summary."
            : "Team summary could not be loaded."}
        </span>
      </div>
      <Button type="button" variant="outline" size="sm-touch" onClick={onRetry}>
        {forbidden ? "Check access again" : "Try again"}
      </Button>
    </div>
  );
}

function buildSummaries(team: CarrierTeamMember[]): CarrierSummary[] {
  const byCarrier = new Map<string, CarrierSummary>();
  for (const member of team) {
    for (const m of member.memberships) {
      const entry = byCarrier.get(m.carrierId) ?? {
        carrierId: m.carrierId,
        carrierName: m.carrier.name,
        carrierAdminCount: 0,
        dispatcherCount: 0,
      };
      if (m.role === "CARRIER_OWNER") entry.carrierAdminCount += 1;
      else if (m.role === "CARRIER_DISPATCHER") entry.dispatcherCount += 1;
      byCarrier.set(m.carrierId, entry);
    }
  }
  return Array.from(byCarrier.values());
}
